import { useState, useEffect, useRef } from "react";

// ═══════════════════════════════════════════════════════════════════════════════
// COPYCAT — LOGIN PAGE
// Demo credentials — no backend required for HackIllinois 2026
// ═══════════════════════════════════════════════════════════════════════════════

// ── DEMO USERS — hardcoded for hackathon, no backend needed ──────────────────
const DEMO_USERS = [
  { email: "inspector@cat.com",  password: "caterpillar", name: "J. Martinez", role: "Field Inspector" },
  { email: "demo@copycat.ai",    password: "demo1234",    name: "Demo User",   role: "Inspector" },
  { email: "admin@copycat.ai",   password: "admin123",    name: "Admin",       role: "Fleet Manager" },
];

// ─── Animated Hex Grid Background ───
function HexGrid() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let frame;
    const resize = () => {
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
      ctx.scale(2, 2);
    };
    resize();
    window.addEventListener("resize", resize);
    const hexSize = 30;
    const cols = Math.ceil(canvas.offsetWidth / (hexSize * 1.75)) + 2;
    const rows = Math.ceil(canvas.offsetHeight / (hexSize * 1.55)) + 2;
    const drawHex = (cx, cy, size, alpha) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const x = cx + size * Math.cos(angle);
        const y = cy + size * Math.sin(angle);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = `rgba(255, 203, 5, ${alpha})`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    };
    let time = 0;
    const animate = () => {
      time += 0.003;
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * hexSize * 1.75 + (r % 2) * hexSize * 0.875;
          const y = r * hexSize * 1.55;
          const dist = Math.sqrt((x - canvas.offsetWidth / 2) ** 2 + (y - canvas.offsetHeight / 2) ** 2);
          const wave = Math.sin(dist * 0.008 - time * 2) * 0.5 + 0.5;
          const alpha = wave * 0.07 + 0.015;
          drawHex(x, y, hexSize, alpha);
        }
      }
      frame = requestAnimationFrame(animate);
    };
    animate();
    return () => { cancelAnimationFrame(frame); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />;
}

function CautionStripe({ position = "top" }) {
  const isTop = position === "top";
  return (
    <div style={{
      position: "absolute", [isTop ? "top" : "bottom"]: 0, left: 0, right: 0, height: 6, overflow: "hidden", zIndex: 10,
      background: `repeating-linear-gradient(${isTop ? "45" : "-45"}deg, #FFCB05 0px, #FFCB05 8px, #000 8px, #000 16px)`,
    }} />
  );
}

function EyeIcon({ open }) {
  if (open) return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function CopyCatLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [lockoutCountdown, setLockoutCountdown] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const emailRef = useRef(null);

  const catYellow = "#FFCB05";
  const catGold = "#F4A100";

  useEffect(() => {
    setMounted(true);
    emailRef.current?.focus();
    // Show hint after 3 seconds to help demo
    const t = setTimeout(() => setShowHint(true), 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!lockoutUntil) return;
    const tick = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000));
      setLockoutCountdown(remaining);
      if (remaining <= 0) { setLockoutUntil(null); setFailedAttempts(0); clearInterval(tick); }
    }, 1000);
    return () => clearInterval(tick);
  }, [lockoutUntil]);

  useEffect(() => {
    const newErrors = {};
    if (touched.email && !email.trim()) newErrors.email = "Email is required";
    if (touched.password && !password) newErrors.password = "Password is required";
    setErrors(newErrors);
  }, [email, password, touched]);

  const handleBlur = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const handleSubmit = async () => {
    if (lockoutUntil && Date.now() < lockoutUntil) return;
    setTouched({ email: true, password: true });
    if (!email.trim() || !password) {
      setErrors({ email: !email.trim() ? "Email is required" : null, password: !password ? "Password is required" : null });
      return;
    }

    setIsLoading(true);
    setApiError("");

    // Simulate network delay for realism
    await new Promise(r => setTimeout(r, 900));

    // ── Check against demo users (no backend needed) ──
    const user = DEMO_USERS.find(
      u => u.email.toLowerCase() === email.toLowerCase().trim() && u.password === password
    );

    if (user) {
      sessionStorage.setItem("copycat_user", JSON.stringify({ name: user.name, role: user.role, email: user.email }));
      setUserName(user.name);
      setUserRole(user.role);
      setLoginSuccess(true);
      setFailedAttempts(0);
      setTimeout(() => onLoginSuccess(user), 2000);
    } else {
      const attempts = failedAttempts + 1;
      setFailedAttempts(attempts);
      if (attempts >= 5) {
        const lockoutMs = 5 * 60 * 1000;
        setLockoutUntil(Date.now() + lockoutMs);
        setApiError("Too many failed attempts. Locked for 5 minutes.");
      } else {
        setApiError("Invalid email or password");
      }
    }

    setIsLoading(false);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSubmit(); };
  const isLocked = lockoutUntil && Date.now() < lockoutUntil;

  // ── Fill demo credentials with one tap ──
  const fillDemo = () => {
    setEmail("inspector@cat.com");
    setPassword("caterpillar");
    setApiError("");
    setTouched({});
    setErrors({});
  };

  // ─── SUCCESS SCREEN ───
  if (loginSuccess) {
    return (
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        background: "#0a0a0a", color: "#e5e5e5", height: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
        maxWidth: 430, margin: "0 auto",
      }}>
        <HexGrid />
        <div style={{ textAlign: "center", zIndex: 10, padding: 40, animation: "successIn 0.6s cubic-bezier(0.22, 1, 0.36, 1)" }}>
          <div style={{
            width: 80, height: 80, borderRadius: 20,
            background: `linear-gradient(135deg, ${catYellow}, ${catGold})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 24px", boxShadow: `0 8px 40px ${catYellow}44`,
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 6 }}>Welcome back</div>
          <div style={{ fontSize: 15, color: catYellow, fontWeight: 700 }}>{userName}</div>
          <div style={{ fontSize: 9, color: "#444", letterSpacing: 2, marginTop: 4 }}>{userRole.toUpperCase()}</div>
          <div style={{ fontSize: 11, color: "#555", letterSpacing: 2, marginTop: 20 }}>LOADING INSPECTION DASHBOARD...</div>
          <div style={{ width: 200, height: 3, background: "#1a1a1a", borderRadius: 2, margin: "14px auto 0", overflow: "hidden" }}>
            <div style={{ width: "100%", height: "100%", background: `linear-gradient(90deg, ${catYellow}, ${catGold})`, animation: "loadBar 1.8s ease-in-out forwards" }} />
          </div>
        </div>
        <style>{`
          @keyframes successIn { from { opacity:0; transform:scale(0.9) translateY(20px); } to { opacity:1; transform:none; } }
          @keyframes loadBar { 0% { transform:translateX(-100%); } 100% { transform:translateX(0%); } }
        `}</style>
      </div>
    );
  }

  // ─── LOGIN FORM ───
  return (
    <div style={{
      fontFamily: "'IBM Plex Mono', monospace",
      background: "#0a0a0a", color: "#e5e5e5", minHeight: "100vh",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden", padding: "20px",
      maxWidth: 430, margin: "0 auto",
    }}>
      <HexGrid />
      <CautionStripe position="top" />
      <CautionStripe position="bottom" />

      <div style={{
        width: "100%", maxWidth: 390, position: "relative", zIndex: 10,
        opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
      }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ width: 44, height: 44, background: catYellow, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 20px ${catYellow}33` }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: -1.5, color: "#fff", lineHeight: 1 }}>
              COPY<span style={{ color: catYellow }}>CAT</span>
            </div>
          </div>
          <div style={{ fontSize: 10, color: "#444", letterSpacing: 4, fontWeight: 600 }}>FIELD INSPECTION SYSTEM</div>
        </div>

        {/* Demo hint pill — appears after 3s */}
        {showHint && (
          <div onClick={fillDemo} style={{
            marginBottom: 16, padding: "10px 14px", borderRadius: 10, cursor: "pointer",
            background: "rgba(255,203,5,0.06)", border: "1px solid rgba(255,203,5,0.2)",
            display: "flex", alignItems: "center", gap: 10, transition: "background 0.2s",
            animation: "fadein 0.4s ease",
          }}>
            <span style={{ fontSize: 14 }}>⚡</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: catYellow, fontWeight: 700, letterSpacing: 1 }}>DEMO CREDENTIALS</div>
              <div style={{ fontSize: 8, color: "#555", marginTop: 2 }}>inspector@cat.com · caterpillar</div>
            </div>
            <div style={{ fontSize: 8, color: catYellow, letterSpacing: 1 }}>TAP TO FILL →</div>
          </div>
        )}

        {/* Form Card */}
        <div style={{
          background: "#0e0e0e", border: "1px solid #1a1a1a", borderRadius: 16,
          padding: "28px 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.6)", position: "relative", overflow: "hidden",
        }}>
          {/* Top accent */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${catYellow}, transparent)` }} />

          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: "#555", marginBottom: 22, textAlign: "center" }}>
            INSPECTOR LOGIN
          </div>

          {/* Error */}
          {apiError && (
            <div style={{
              padding: "10px 12px", marginBottom: 16, borderRadius: 8,
              background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)",
              display: "flex", alignItems: "center", gap: 8,
              animation: "shakeIn 0.4s ease",
            }}>
              <span style={{ fontSize: 14 }}>⚠</span>
              <div>
                <div style={{ fontSize: 11, color: "#ef4444", fontWeight: 600 }}>{apiError}</div>
                {failedAttempts > 0 && failedAttempts < 5 && (
                  <div style={{ fontSize: 9, color: "#555", marginTop: 2 }}>{5 - failedAttempts} attempts remaining</div>
                )}
              </div>
            </div>
          )}

          {/* Lockout */}
          {isLocked && (
            <div style={{ padding: "12px", marginBottom: 16, borderRadius: 8, textAlign: "center", background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)" }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>🔒</div>
              <div style={{ fontSize: 11, color: "#f97316", fontWeight: 700 }}>ACCOUNT LOCKED</div>
              <div style={{ fontSize: 18, color: "#f97316", fontWeight: 900, marginTop: 3 }}>
                {Math.floor(lockoutCountdown / 60)}:{String(lockoutCountdown % 60).padStart(2, "0")}
              </div>
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9, fontWeight: 700, letterSpacing: 2, color: "#555", marginBottom: 7 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,4 12,13 2,4" /></svg>
              EMAIL
            </label>
            <input
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setApiError(""); }}
              onBlur={() => handleBlur("email")}
              onKeyDown={handleKeyDown}
              placeholder="inspector@cat.com"
              disabled={isLoading || isLocked}
              autoComplete="email"
              style={{
                width: "100%", padding: "13px 14px", background: "#0a0a0a",
                border: `1.5px solid ${errors.email && touched.email ? "#ef4444" : "#222"}`,
                borderRadius: 8, color: "#e5e5e5", fontSize: 13, fontFamily: "inherit",
                outline: "none", boxSizing: "border-box", transition: "border-color 0.2s",
                opacity: isLocked ? 0.4 : 1,
              }}
              onFocus={(e) => { e.target.style.borderColor = catYellow; e.target.style.boxShadow = `0 0 0 3px ${catYellow}11`; }}
              onBlurCapture={(e) => { e.target.style.borderColor = errors.email ? "#ef4444" : "#222"; e.target.style.boxShadow = "none"; }}
            />
            {errors.email && touched.email && (
              <div style={{ fontSize: 10, color: "#ef4444", marginTop: 5 }}>● {errors.email}</div>
            )}
          </div>

          {/* Password */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9, fontWeight: 700, letterSpacing: 2, color: "#555", marginBottom: 7 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
              PASSWORD
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setApiError(""); }}
                onBlur={() => handleBlur("password")}
                onKeyDown={handleKeyDown}
                placeholder="••••••••"
                disabled={isLoading || isLocked}
                autoComplete="current-password"
                style={{
                  width: "100%", padding: "13px 44px 13px 14px", background: "#0a0a0a",
                  border: `1.5px solid ${errors.password && touched.password ? "#ef4444" : "#222"}`,
                  borderRadius: 8, color: "#e5e5e5", fontSize: 13, fontFamily: "inherit",
                  outline: "none", boxSizing: "border-box", transition: "border-color 0.2s",
                  opacity: isLocked ? 0.4 : 1,
                }}
                onFocus={(e) => { e.target.style.borderColor = catYellow; e.target.style.boxShadow = `0 0 0 3px ${catYellow}11`; }}
                onBlurCapture={(e) => { e.target.style.borderColor = errors.password ? "#ef4444" : "#222"; e.target.style.boxShadow = "none"; }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#444", cursor: "pointer", padding: 4, display: "flex" }}>
                <EyeIcon open={showPassword} />
              </button>
            </div>
            {errors.password && touched.password && (
              <div style={{ fontSize: 10, color: "#ef4444", marginTop: 5 }}>● {errors.password}</div>
            )}
          </div>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={isLoading || isLocked}
            style={{
              width: "100%", padding: "15px", border: "none", borderRadius: 10,
              background: isLoading || isLocked ? "#2a2a2a" : `linear-gradient(135deg, ${catYellow}, ${catGold})`,
              color: isLoading || isLocked ? "#555" : "#000",
              fontSize: 12, fontWeight: 900, letterSpacing: 2, fontFamily: "inherit",
              cursor: isLoading || isLocked ? "not-allowed" : "pointer",
              boxShadow: isLoading || isLocked ? "none" : `0 4px 20px ${catYellow}22`,
              transition: "all 0.3s",
            }}>
            {isLoading ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: "spin 0.8s linear infinite" }}>
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" strokeLinecap="round" />
                </svg>
                AUTHENTICATING...
              </span>
            ) : isLocked ? "ACCOUNT LOCKED" : "SIGN IN →"}
          </button>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button onClick={fillDemo} style={{ background: "none", border: "none", color: "#333", fontSize: 10, cursor: "pointer", fontFamily: "inherit", letterSpacing: 1 }}
              onMouseEnter={(e) => e.currentTarget.style.color = catYellow}
              onMouseLeave={(e) => e.currentTarget.style.color = "#333"}>
              USE DEMO CREDENTIALS
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <div style={{ fontSize: 9, color: "#2a2a2a", letterSpacing: 3, marginBottom: 4 }}>CATERPILLAR® TRACK · HACKILLINOIS 2026</div>
          <div style={{ fontSize: 8, color: "#1e1e1e" }}>CopyCat AI Inspection System</div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        body { margin: 0; background: #0a0a0a; }
        input::placeholder { color: #2a2a2a; }
        input:disabled { cursor: not-allowed; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadein { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }
        @keyframes shakeIn { 0%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(2px)} 100%{transform:translateX(0)} }
        @media (max-width: 480px) { input { font-size: 16px !important; } }
      `}</style>
    </div>
  );
}